import {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonText,
    IonToolbar,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { wifiOutline } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import LoadableContent from '../../../components/content/LoadableContent';
import LoadingButton from '../../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../../components/page/InterventionPage';
import { IInstallationRouteProps } from '../../../interfaces/IRoute';
import { ESPConstants } from '../../../provisioning/ESPConstants';
import { ESPProvisionManager } from '../../../provisioning/ESPProvisionManager';
import {
    meshCompletedAtom,
    roomsWithDevicesJobDetailsAndStateAtom,
    skipBleValidationAtom,
} from '../../../store/Atoms';
import { getPoPForDevice, trackActionsRequest } from '../../../store/Requests';
import { ODOO_INST_TYPE } from '../../../utils/constants';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';

enum LoadingType {
    SCAN = 'scan',
    POP = 'pop',
    NONE = 'none',
}

const Home: React.FC<IInstallationRouteProps> = ({ match }) => {
    const jobId = +match.params.jobId;
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const [loadingType, setLoadingType] = useState(LoadingType.NONE);
    const [popError, setPopError] = useState(false);
    const [isBLEInitialized, setIsBLEInitialized] = useState(false);
    const [value] = useAtom(roomsWithDevicesJobDetailsAndStateAtom);
    const [skipBleValidation] = useAtom(skipBleValidationAtom);
    const [meshCompleted] = useAtom(meshCompletedAtom);
    const modal = useRef<HTMLIonModalElement>(null);
    const [openModal, setOpenModal] = useState(false);

    const jobDetails = useMemo(
        () => (value.state === 'hasData' ? value.data.jobDetails : undefined),
        [value]
    );
    const rooms = useMemo(
        () => (value.state === 'hasData' ? value.data.rooms : []),
        [value]
    );
    const devices = useMemo(
        () => rooms.flatMap((room) => room.devices),
        [rooms]
    );
    const macAddresses = useMemo(() => {
        return devices.map((device) => device.macAddress);
    }, [devices]);
    const prevDevicesMacAddresses = useMemo(() => {
        return devices
            .filter((device) => !device.isNew)
            .map((device) => device.macAddress);
    }, [devices]);

    const checkDeviceState = useCallback(async () => {
        try {
            const provisionManager = ESPProvisionManager.getInstance();
            const deviceState = await provisionManager
                .getEspDevice()
                .getDeviceState();
            const connectedMacAddress = provisionManager
                .getEspDevice()
                .getDeviceMacAddress();

            if (
                !skipBleValidation &&
                jobDetails!.typeInstallation === ODOO_INST_TYPE.REPAIR &&
                prevDevicesMacAddresses.length > 0
            ) {
                if (!prevDevicesMacAddresses.includes(connectedMacAddress)) {
                    provisionManager.getEspDevice().disconnectDevice();
                    throw new Error(
                        t`This intervention is a repair. You need to connect to a device already in the mesh.`
                    );
                }

                if (
                    prevDevicesMacAddresses.includes(connectedMacAddress) &&
                    deviceState !== ESPConstants.ProvisionState.IDLE_CONFIGURED
                ) {
                    provisionManager.getEspDevice().disconnectDevice();
                    throw new Error(
                        t`This device is not configured. Please try to connect to another device already in the mesh. If you reset all the devices in the mesh, please call your supervisor.`
                    );
                }
            }

            if (deviceState === ESPConstants.ProvisionState.IDLE_NEW) {
                router.push(
                    `/${jobId}/installation/mesh/select-network`,
                    'forward'
                );
            } else {
                router.push(
                    `/${jobId}/installation/mesh/maintenance`,
                    'forward'
                );
            }
        } catch (e) {
            setPopError(false);
            const error = e as Error;
            presentToast({
                message: error.message,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
    }, [
        jobDetails,
        jobId,
        presentToast,
        prevDevicesMacAddresses,
        router,
        skipBleValidation,
    ]);

    const getAndSetPoP = useCallback(async () => {
        try {
            const provisionManager = ESPProvisionManager.getInstance();
            const proofOfPossession = await getPoPForDevice(
                provisionManager.getEspDevice().getDeviceMacAddress()
            );
            if (!proofOfPossession) {
                throw new Error(
                    t`No proof of possession received for this device. Please retry again. If the problem persists please call your supervisor.`
                );
            }
            provisionManager
                .getEspDevice()
                .setProofOfPossession(proofOfPossession);
            trackActionsRequest('set_proof_of_possession');
            await checkDeviceState();
        } catch (e: any) {
            setPopError(true);
            presentToast({
                message:
                    e.reason ||
                    e.message ||
                    t`Oops! Something went wrong while getting the proof of possession`,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
    }, [checkDeviceState, presentToast]);

    const onRetryPop = useCallback(async () => {
        setLoadingType(LoadingType.POP);
        await getAndSetPoP();
        setLoadingType(LoadingType.NONE);
    }, [getAndSetPoP]);

    const onScanDevicesHandler = useCallback(async () => {
        setPopError(false);
        setLoadingType(LoadingType.SCAN);
        try {
            const provisionManager = ESPProvisionManager.getInstance();
            provisionManager.getEspDevice() &&
                provisionManager.getEspDevice().disconnectDevice();
            provisionManager.createESPDevice(
                ESPConstants.TransportType.TRANSPORT_BLE,
                ESPConstants.SecurityType.SECURITY_1
            );

            trackActionsRequest('scan_devices');

            const bleDevice = await provisionManager.searchBleEspDevices();
            await provisionManager.getEspDevice().connectBLEDevice(bleDevice);

            // Get the last digits of the device mac address from the device name
            // Normally the device name is TIKO_<last 6 digits of mac address>
            const macAddressLastDigits = bleDevice.name?.substring(5) || '';
            // Get the mac address of the device we just connected to
            const connectedMacAddress =
                macAddresses.find((macAddress) => {
                    return macAddress.endsWith(macAddressLastDigits);
                }) || '';

            // The mac address is not part of this intervention
            if (!connectedMacAddress) {
                provisionManager.getEspDevice().disconnectDevice();
                throw new Error(t`Could not find mac address`);
            }

            provisionManager
                .getEspDevice()
                .setDeviceMacAddress(connectedMacAddress);

            trackActionsRequest('connect_to_device', {
                deviceName: bleDevice.name,
            });
            await getAndSetPoP();
        } catch (e) {
            const error = e as Error;
            presentToast({
                message: error.message,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setLoadingType(LoadingType.NONE);
    }, [getAndSetPoP, macAddresses, presentToast]);

    useEffect(() => {
        const initBLE = async () => {
            const provisionManager = ESPProvisionManager.getInstance();
            await provisionManager.init();
            setIsBLEInitialized(true);
        };
        initBLE();
    }, []);

    return (
        <InterventionPage title={t`Mesh`} withPadding>
            <LoadableContent value={value}>
                <IonCard>
                    <IonCardContent>
                        <IonText>
                            <IonIcon
                                icon={wifiOutline}
                                size="large"
                                className="ion-float-left ion-padding-end"
                            ></IonIcon>
                            <Trans>
                                After wiring the D-Boxes, press the button of
                                the LTE D-Box (Master) for 5 seconds to start
                                the network provisioning. It should flash
                                purple.
                            </Trans>
                            <br />
                            <p>
                                <Trans>
                                    You will be prompted to pair your phone
                                    with the device to transfer data and then
                                    to enter in the customer's Wi-Fi
                                    credentials. Please explain to the customer
                                    that this information does not leave their
                                    house and is transferred directly to the
                                    devices.
                                </Trans>
                            </p>
                        </IonText>
                        <IonButton
                            className="ion-margin-top"
                            expand="block"
                            onClick={() => {
                                setOpenModal(true);
                            }}
                        >
                            <Trans>Troubleshooting info</Trans>
                        </IonButton>
                        <IonModal
                            ref={modal}
                            isOpen={openModal}
                            backdropDismiss={false}
                            onDidDismiss={() => {
                                setOpenModal(false);
                            }}
                        >
                            <IonHeader>
                                <IonToolbar>
                                    <IonButtons slot="end">
                                        <IonButton
                                            onClick={() =>
                                                modal.current?.dismiss()
                                            }
                                        >
                                            Close
                                        </IonButton>
                                    </IonButtons>
                                </IonToolbar>
                            </IonHeader>

                            <IonContent className="ion-padding">
                                <h4>
                                    <Trans>
                                        If you have trouble connecting to a
                                        device via Bluetooth:
                                    </Trans>
                                </h4>
                                <IonList>
                                    <IonItem>
                                        <IonLabel>
                                            <Trans>
                                                Make sure you stay near the
                                                device (prepare Wi-Fi
                                                credentials in advance)
                                            </Trans>
                                        </IonLabel>
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel>
                                            <Trans>
                                                Make sure you are using Google
                                                Chrome browser on an Android
                                                phone
                                            </Trans>
                                        </IonLabel>
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel>
                                            <Trans>
                                                If the problem persists, try
                                                toggle Bluetooth on and off on
                                                your phone
                                            </Trans>
                                        </IonLabel>
                                    </IonItem>
                                </IonList>
                            </IonContent>
                        </IonModal>
                    </IonCardContent>
                </IonCard>
                {popError && (
                    <LoadingButton
                        className="ion-margin-top"
                        loading={loadingType === LoadingType.POP}
                        onClick={onRetryPop}
                    >
                        <Trans>Retry PoP</Trans>
                    </LoadingButton>
                )}
                {isBLEInitialized ? (
                    <LoadingButton
                        className="ion-margin-top"
                        onClick={onScanDevicesHandler}
                        loading={loadingType === LoadingType.SCAN}
                    >
                        <Trans>Scan Devices</Trans>
                    </LoadingButton>
                ) : (
                    <IonCard
                        color="warning"
                        className="ion-no-margin ion-margin-top"
                    >
                        <IonCardContent>
                            <Trans>
                                Your device does not support Bluetooth Low
                                Energy (BLE).
                            </Trans>
                        </IonCardContent>
                    </IonCard>
                )}
                {(jobDetails?.typeInstallation === ODOO_INST_TYPE.REPAIR ||
                    meshCompleted) && (
                    <div className="ion-margin-top">
                        <IonButton
                            expand="block"
                            routerLink={`/${jobId}/installation/mesh/status`}
                            routerDirection="forward"
                        >
                            <Trans>Mesh Status</Trans>
                        </IonButton>
                    </div>
                )}
            </LoadableContent>
        </InterventionPage>
    );
};

export default Home;
